import { useMemo } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { screen } from '@/components/common/breakpointsV2';
import { labelSmall, subheadingMedium } from '@/components/common/elements';
import { determineDestination, stringToId } from '@/components/common/utils';
import ChevronIconSvg from '@/public/images/chevron-v2.svg';

import { SubNavigation } from './SubNavigation';

import type { Theme } from '@/components/layout/common';
import type { ColumnDataProps } from '@/components/layout/NavigationBarV2/SubNavigation/SubNavigation';
import type { MouseEvent } from 'react';

interface ButtonProps {
  isAnySubNavOpen: boolean;
  title?: string;
  itemId: string;
  onClick?: (key: string) => void;
}

const NavigationButton = ({
  isAnySubNavOpen,
  title,
  itemId,
  onClick,
  ...rest
}: ButtonProps) => {
  const titleString = title ? `-${stringToId(title)}` : null;
  return (
    <button
      type="button"
      onClick={() => onClick(itemId)}
      className="tracking-nav__button"
      aria-expanded={isAnySubNavOpen}
      aria-controls={`sub-navigation${titleString}`}
      aria-label={`More ${title} pages`}
      {...rest}
    >
      <span>{title}</span>
      <ChevronIconSvg />
    </button>
  );
};

interface NavigationLinkProps {
  linkHref: string;
  title?: string;
  itemId?: string;
  isThisSubNavOpen?: boolean;
  onMouseEnterNavLink?: (
    event: MouseEvent<HTMLAnchorElement>,
    key: string,
  ) => void;
}

const NavigationLink = ({
  linkHref,
  title,
  itemId,
  onMouseEnterNavLink,
  isThisSubNavOpen,
}: NavigationLinkProps) => {
  const destination = determineDestination(linkHref, false);
  const { asPath } = useRouter();
  const ariaCurrent = useMemo<'page' | null>(
    () => (linkHref === asPath ? 'page' : null),
    [linkHref, asPath],
  );

  return (
    <TopLevelNavLink
      href={linkHref}
      {...(onMouseEnterNavLink && {
        onMouseEnter: (e) => onMouseEnterNavLink(e, itemId),
      })}
      className="tracking-nav__link"
      target={destination.target}
      aria-label={`${title}`}
      aria-current={ariaCurrent}
    >
      {title}
      {isThisSubNavOpen && <Indicator layoutId="nav-indicator" />}
    </TopLevelNavLink>
  );
};
interface TopLevelNavItemProps {
  isThisSubNavOpen?: boolean;
  linkHref: string;
  columns?: ColumnDataProps;
  topLevelLinkOnly?: boolean;
  isAnySubNavOpen: boolean;
  itemId: string;
  isDesktop: boolean;
  title: string;
  theme: Theme;
  onMouseEnterNavLink?: (
    event: MouseEvent<HTMLAnchorElement>,
    key: string,
  ) => void;
  onClickNavLink?: (key: string) => void;
}

const navLinkOpacity = (isThisSubNavOpen: boolean, theme: Theme) => {
  const opacityLevel = theme === 'light' || isThisSubNavOpen ? 0.95 : 0.85;
  return css`
    opacity: ${opacityLevel};
  `;
};

export const TopLevelNavItem = ({
  isThisSubNavOpen,
  linkHref,
  columns,
  topLevelLinkOnly,
  isAnySubNavOpen,
  itemId,
  isDesktop,
  title,
  theme,
  onMouseEnterNavLink,
  onClickNavLink,
}: TopLevelNavItemProps) => {
  return (
    <TopLevelNavItemContainer
      isThisSubNavOpen={isThisSubNavOpen}
      isAnySubNavOpen={isAnySubNavOpen}
      topLevelLinkOnly={topLevelLinkOnly}
      theme={theme}
    >
      {isDesktop && (
        <SubnavList>
          <SubnavListItem>
            <NavigationLink
              onMouseEnterNavLink={onMouseEnterNavLink}
              itemId={itemId}
              linkHref={linkHref}
              title={title}
              isThisSubNavOpen={isThisSubNavOpen}
            />
          </SubnavListItem>
          {!topLevelLinkOnly && (
            <SubnavToggleWrapper>
              <SubnavToggleBtn
                itemId={itemId}
                isAnySubNavOpen={isAnySubNavOpen}
                onClick={onClickNavLink}
                title={title}
              />
            </SubnavToggleWrapper>
          )}
        </SubnavList>
      )}
      {!isDesktop && topLevelLinkOnly && (
        <NavigationLink
          linkHref={linkHref}
          title={title}
          isThisSubNavOpen={isThisSubNavOpen}
        />
      )}
      {!isDesktop && !topLevelLinkOnly && (
        <TopLevelNavButton
          itemId={itemId}
          isAnySubNavOpen={isAnySubNavOpen}
          onClick={onClickNavLink}
          title={title}
        />
      )}
      <AnimatePresence custom={{ isAnySubNavOpen }}>
        {!topLevelLinkOnly && (
          <SubNavigation
            isAnySubNavOpen={isAnySubNavOpen}
            title={title}
            columns={columns}
            isThisSubNavOpen={isThisSubNavOpen}
          />
        )}
      </AnimatePresence>
    </TopLevelNavItemContainer>
  );
};

const navLinkUnderlineTransition = 'text-decoration-color 200ms';

const TopLevelNavButton = styled(NavigationButton)`
  ${subheadingMedium}
  font-weight: var(--font-weight-bold);
  color: inherit;
  width: 100%;
  background-color: var(--colour-white);
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-250) var(--spacing-200);
  border-bottom: 1px solid var(--colour-border-subtle);

  svg {
    fill: var(--colour-deep-space);
    width: 1.75rem;
    height: 1.75rem;
  }

  ${screen.md} {
    display: none;
  }
`;

const TopLevelNavLink = styled(motion(Link))`
  ${subheadingMedium}
  font-weight: var(--font-weight-bold);
  color: inherit;
  width: 100%;
  height: 100%;
  background-color: var(--colour-white);
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-250) var(--spacing-200);
  border-bottom: 1px solid var(--colour-border-subtle);
  text-decoration: none;
  position: relative;

  ${screen.md} {
    ${labelSmall}
    font-weight: var(--font-weight-bold);
    background-color: transparent;
    position: relative;
    justify-content: center;
    padding: 0;
    border-bottom: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Indicator = styled(motion.div)`
  ${screen.md} {
    background: var(--colour-deep-space);
    position: absolute;
    width: 100%;
    height: 2px;
    bottom: 0;
    left: 0;
  }
`;

const TopLevelNavItemContainer = styled.li<{
  isThisSubNavOpen: boolean;
  isAnySubNavOpen: boolean;
  topLevelLinkOnly: boolean;
  theme: Theme;
}>`
  ${TopLevelNavButton}, ${TopLevelNavLink} {
    ${({ isThisSubNavOpen, theme }) => navLinkOpacity(isThisSubNavOpen, theme)}
    transition: opacity 0.25s ease-out;

    svg {
      transform: ${({ isThisSubNavOpen }) =>
        isThisSubNavOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      transition: transform 0.25s ease-out;
    }
  }

  ${screen.md} {
    border: none;
    display: flex;
    align-items: stretch;

    transition: border-bottom 0.125s ease-out;

    &:not(:last-child) {
      margin-right: var(--spacing-300);
    }

    ${TopLevelNavLink} {
      transition:
        opacity 0.25s
          ${({ isThisSubNavOpen }) =>
            isThisSubNavOpen ? 'ease-out' : 'ease-in'},
        ${navLinkUnderlineTransition};

      &::before,
      &::after {
        content: '';
        background-color: transparent;
        top: -25px;
        bottom: 0;
        position: absolute;
        display: none;
        width: calc(var(--spacing-1000));
        height: calc(var(--spacing-1000) * 3);
        z-index: 1;
        user-select: none;
      }

      &::before {
        left: calc(var(--spacing-1000) * -1);
        transform: rotate(70deg);
      }

      &::after {
        right: calc(var(--spacing-1000) * -1);
        transform: rotate(-70deg);
      }
    }

    &:hover ${TopLevelNavLink}::before, &:hover ${TopLevelNavLink}::after {
      display: ${({ isAnySubNavOpen, topLevelLinkOnly }) =>
        isAnySubNavOpen && !topLevelLinkOnly ? 'block' : 'none'};
    }
  }
`;

const SubnavToggleWrapper = styled.li`
  position: relative;
  align-items: center;
`;

const SubnavList = styled.ul`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SubnavListItem = styled.li`
  display: flex;
  align-items: center;
  height: 100%;
`;

const SubnavToggleBtn = styled(NavigationButton)<{ isAnySubNavOpen: boolean }>`
  ${screen.md} {
    background-color: transparent;
    border: none;
    pointer-events: none;
    right: -1.1rem;
    opacity: 0;

    &:focus {
      pointer-events: auto;
      opacity: 1;
    }
    svg {
      fill: var(--colour-deep-space);
      width: 0.6rem;
      height: 0.6rem;
      transform: ${({ isAnySubNavOpen }) =>
        isAnySubNavOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      transition: transform 0.25s ease-out;
    }

    span {
      display: none;
    }
  }
`;
